import { useId, type ReactNode } from "react";
import { Button, cx } from "../../ui";
import { actionLabel, formatWhen, isExpired, timeLeft, viaLabel, type Approval, type Review } from "./model";

const STATUS_LABELS: Record<Approval["status"], string> = {
  pending: "Waiting for you",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
  failed: "Failed",
};

function Field({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[6rem_minmax(0,1fr)] gap-2">
      <dt className="text-muted">{term}</dt>
      <dd className="min-w-0 break-words text-text">{children}</dd>
    </div>
  );
}

/** Pre-formatted text, wrapped, in a scroll box: payloads are shown, never rendered. */
function Plain({ label, children }: { label: string; children: string }) {
  return (
    <pre aria-label={label} className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-surface-2 p-3 font-mono text-xs text-text">
      {children}
    </pre>
  );
}

const list = (people: string[]) => (people.length === 0 ? "—" : people.join(", "));

/** The exact payload, per action type, for review before approving. */
export function ReviewView({ review }: { review: Review }) {
  switch (review.kind) {
    case "send":
      return (
        <div className="flex flex-col gap-3">
          <dl className="flex flex-col gap-1 text-sm">
            <Field term="From">{review.from}</Field>
            <Field term="To">{list(review.to)}</Field>
            {review.cc.length === 0 ? null : <Field term="Cc">{list(review.cc)}</Field>}
            {review.bcc.length === 0 ? null : <Field term="Bcc">{list(review.bcc)}</Field>}
            <Field term="Subject">{review.subject.length === 0 ? "(no subject)" : review.subject}</Field>
          </dl>
          <Plain label={review.bodyFromHtml ? "Message text (from HTML)" : "Message text"}>{review.body}</Plain>
          {review.html === null ? null : (
            <details className="text-sm">
              <summary className="cursor-pointer text-muted">Show the HTML source</summary>
              <Plain label="HTML source">{review.html}</Plain>
            </details>
          )}
        </div>
      );
    case "forward":
      return (
        <div className="flex flex-col gap-3">
          <dl className="flex flex-col gap-1 text-sm">
            <Field term="Message">{review.messageId}</Field>
            <Field term="To">{list(review.to)}</Field>
          </dl>
          {review.note.length === 0 ? null : <Plain label="Note">{review.note}</Plain>}
        </div>
      );
    case "bulk":
      return (
        <div className="flex flex-col gap-2 text-sm">
          <p className="text-text">
            Matches <strong>{review.matched}</strong> {review.matched === 1 ? "item" : "items"}
            {review.sample.length < review.matched ? `; the first ${review.sample.length}:` : ":"}
          </p>
          <ul aria-label="Sample" className="list-disc pl-5 text-text">
            {review.sample.map((line, i) => (
              <li key={i} className="break-words">
                {line}
              </li>
            ))}
          </ul>
        </div>
      );
    case "json":
      return <Plain label="Details">{review.text}</Plain>;
  }
}

export interface CardActions {
  onApprove(): void;
  onReject(): void;
  busy: boolean;
}

/** One approval: the action in plain words, who asked and when, and the payload to review. */
export function ApprovalCard({ approval, review, now, actions }: { approval: Approval; review: Review; now: Date; actions: CardActions | null }) {
  const titleId = useId();
  const expired = approval.status === "pending" && isExpired(approval, now);
  const left = timeLeft(approval.expiresAt, now);

  return (
    <article aria-labelledby={titleId} className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-4 sm:p-5">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 id={titleId} className="text-base font-semibold text-text">
            {actionLabel(approval.action)}
          </h2>
          <p className="mt-0.5 break-words text-sm text-muted">{approval.summary}</p>
        </div>
        <span
          className={cx(
            "shrink-0 rounded-full border border-border px-2 py-0.5 text-xs font-medium",
            approval.status === "failed" || expired ? "text-danger" : "text-muted",
          )}
        >
          {expired ? "Expired" : STATUS_LABELS[approval.status]}
        </span>
      </header>

      <dl className="flex flex-col gap-1 text-sm">
        <Field term="Asked by">{viaLabel(approval.requestedVia)}</Field>
        <Field term="Asked">{formatWhen(approval.createdAt)}</Field>
        {approval.status === "pending" ? (
          <Field term="Expires">{left === null ? "Expired" : `${left} (${formatWhen(approval.expiresAt)})`}</Field>
        ) : (
          <Field term="Decided">{approval.decidedAt === null ? "—" : formatWhen(approval.decidedAt)}</Field>
        )}
      </dl>

      <ReviewView review={review} />

      {approval.status === "failed" && approval.error !== null ? (
        <p className="break-words text-sm text-danger">Error: {approval.error}</p>
      ) : null}
      {approval.status === "approved" && approval.result !== null ? <Plain label="Result">{JSON.stringify(approval.result, null, 2)}</Plain> : null}

      {actions === null ? null : expired ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="flex-1 text-sm text-muted">This request expired; ask again from where it came from.</p>
          <Button disabled={actions.busy} onClick={actions.onReject}>
            Dismiss
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={actions.busy} onClick={actions.onApprove}>
            Approve
          </Button>
          <Button variant="danger" disabled={actions.busy} onClick={actions.onReject}>
            Reject
          </Button>
        </div>
      )}
    </article>
  );
}
